/**
 * @file OrderLinesGrid.js
 * @version 2.28.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-13
 * @modified 2026-11-03
 * @description A document's lines, as one editable table — for customer
 *              orders, invoices, purchase orders and bills.
 *
 *              Every cell is editable except Tax and Line Total, which are
 *              derived — and a derived figure that can be typed over is a figure
 *              nobody can trust, so they stay calculated.
 *
 *              A line needs an ITEM or a DESCRIPTION, not both and not neither.
 *              That is what makes a typed one-off line possible at all, and it
 *              is validated by each host before saving.
 *
 *              ONE GRID, FOUR DOCUMENTS
 *
 *              `variant` picks the columns and the rules; the line shape stays
 *              the customer order's own (see utils/lineItems for how each
 *              document maps in and out):
 *
 *              2.24.0 (2026-10-28, Cam — "so long as the user is totally
 *              clear what £/ unit UNITS actually are (i.e. whether £100/T or
 *              /Tote or /25kg Bag etc.)"): under every Unit Cost / Unit Sale /
 *              Unit Price input, what the figure is per — "/T", "/500kg
 *              tote", "/25kg bag", "each", "/kg" (a bill line billed per kg)
 *              — and for a pack, what it comes to a tonne ("= £738.00/T"), so
 *              a tonne's price typed on a tote line shows itself; "unit not
 *              set" where nothing gives one (nothing on a bill's charge lines)
 *              (utils/unitPriceLabel). `priceUnitFacts(line)` lets a host say
 *              what a line's price is per (Add Bill: as billed, else its PO
 *              line's unit). An invoice made from an order says under each
 *              quantity whether it is the supplied quantity or the ordered one
 *              with none recorded (`supplied_recorded` on the line).
 *
 *              2.23.0 (2026-10-28, Cam — Add Bill's cost check):
 *              `pricePlaceholder(line)` — the price cell's placeholder for one
 *              line ("Choose a cost" while a bill line's cost is unchosen).
 *
 *              2.14.0 (2026-10-14, Cam): lineTotals honours a line's own VAT
 *              figure (`_vat`, utils/billLineSplit) — a bill line split over
 *              several PO lines whose VAT carries the penny the split's
 *              rounding moved — while its amount and tax type are unchanged.
 *
 *              2.13.0 (2026-10-12, Cam): the invoice grid has Units too —
 *              the item's, a fixed unit, or None — greyed with
 *              `unitsUnavailable` until invoice lines can store one.
 *
 *              2.12.0 (2026-10-12, Cam): Units offers "None — no weight" for
 *              a service or weighbridge charge — stored as unit weight 0
 *              (utils/lineUnits), with no pallets; its Weight and Pallets
 *              cells read 0 and are fixed, and its quantity is a plain count.
 *              The Transport cell is TransportStack: on an empty cell any of
 *              the three asks for the driver first, with "No driver".
 *
 *              2.17.0 (2026-10-16, Cam — Burcombe's QA03476 VAT):
 *              LineTotalsPanel's `perLine` — a bill's VAT added up line by
 *              line, each to the penny, as it is saved and as Xero works it.
 *
 *              2.16.0 (2026-10-16, Cam — Add Bill made simple): three
 *              opt-in hooks for a bill. `cellChoice(line, col)` — a cell whose
 *              PO and bill values differ is wrapped in FieldChoice (a marker,
 *              and a card offering the PO's, the bill's or your own; text and
 *              number cells open it on focus, pickers from the marker).
 *              `groupHeader(line, index)` — a heading row above a line (a
 *              haulier's job: date, their ref, our document, delivered to).
 *              `lineFlags` may give a line several flags, each with its own
 *              actions (small buttons) or a control — gentle notes, never a
 *              gate.
 *
 *              2.21.0 (2026-10-28, Cam — "make units a mandatory field
 *              when creating customer orders"): on an order or PO line,
 *              picking an item puts the item's unit on the line
 *              (utils/lineUnitRequired.explicitUnitKg — its tote, its unit
 *              weight, a tonne for a bulk code), and choosing the item's own
 *              unit in Units keeps it on the line rather than going back to
 *              "the item's". `requireUnits`: a line with no known unit has
 *              its Units cell outlined in the error colour with the reason.
 *
 *              2.26.0 (2026-10-30, Cam — SO-D10442, units corrected after
 *              delivery): with `closed` a change of Units / Weight / Other
 *              never re-costs the line here — the host asks what its price
 *              was per and works the money out (utils/unitsAfterDelivery);
 *              `closed.unitsBlock(line)` greys that line's Units, Weight,
 *              Pallets and Other fields with why they cannot change.
 *
 *              2.15.0 (2026-10-15, Cam): `removeBlock(line)` — why one
 *              line cannot be removed (a PO line bought for an order line);
 *              its delete button is greyed with that reason.
 *
 *              2.10.0 (2026-10-09, Cam): `closed` — the cells a document
 *              delivered or invoiced keeps as they are (quantity, adding and
 *              removing lines, and on an invoiced order the price, tax and
 *              additives). Greyed with the reason, never hidden; the words,
 *              item and supplier stay open for corrections.
 *
 *                order     Item · Desc · Additives · Supplier · Qty · Units · Weight
 *                          · Pallets · Unit Cost · Unit Sale · Tax Type · Tax · Total
 *                          (Desc is two boxes: Sale — the invoice — and
 *                          Purchase — the PO and the supplier's bill)
 *                invoice   Item · Desc · Additives · Supplier · Qty · Units · Unit Price
 *                          · Account · Tax Type · Tax · Total
 *                purchase  Item · Desc · Additives · Transport · Qty · Units · Weight
 *                          · Pallets · Unit Cost · Tax Type · Tax · Total
 *
 *              UNITS, WEIGHT, PALLETS (orders and purchase orders)
 *
 *              Start from the item and can be set per line — for an item with
 *              nothing on file, or a load that differs from the catalogue. A
 *              line holds only what differs: unit_weight_kg / qty_per_pallet /
 *              pallets, blank = the item's. Weight is shown in tonnes and typing
 *              one sets the line's unit weight (weight ÷ qty). "Other" is set by
 *              the pallet, as on the item: qty per pallet and pallet weight.
 *                bill      Item · Desc · Additives · [PO] · Qty · Unit Price · Account
 *                          · Tax Type · Tax · Total
 *                          (PO only when the host passes `poOptions`: which of
 *                          the bill's POs the line bills, or none — 2026-10-03)
 *
 *              Orders and invoices are CUSTOMER documents: an item resolves its
 *              price through the customer's contracts, and a line names its own
 *              supplier. Purchase orders and bills are SUPPLIER documents: the
 *              supplier is fixed in the header, so there is no Supplier column,
 *              additives are scoped to that header supplier, and an item brings
 *              its own cost.
 *
 *              Uses `.line-items-table` from TableStyles. Deliberately NOT
 *              `.order-lines-table`: CustomerOrderModal.css defines that class
 *              unprefixed with `overflow: hidden`, and with no CSS modules in
 *              this client that bleeds into anything reusing the name.
 *
 * 2.18.0 (2026-10-20, Cam): the supplied hint under a pack line's quantity
 * adds the tonnes it comes to ("Supplied 38 · 0.95 T"), and an invoice line in
 * packs or a bill line billed per kg says its tonnes under the input ("= 1.00
 * T") — utils/qtyWithTonnes; the input keeps what is typed.
 *
 * 2.19.0 (2026-10-21, Cam — an order of 40 × 25kg bags of an item costed £464
 * a tonne came out at £464 a bag): a line's cost is per ITS unit
 * (utils/itemCostUnit). On an order line the blank Unit Cost shows the cost it
 * will be raised at — the item's, converted to the line's unit ("11.60", the
 * title saying "£464.00 a tonne on the item = £11.60 a 25kg bag"). Changing a
 * line's Units or Weight re-costs it so the cost per tonne stays the same (the order's Unit Cost, a PO's Unit Cost). An item with
 * a cost and NO unit is asked about on the line — "what is that cost per?",
 * with "Save on the item for next time" (PATCH /api/inventory/:id/unit-weight)
 * — and its cost is not filled in until answered (a PO line picked from it
 * starts blank).
 *
 * 2.20.0 (2026-10-23, Cam — "the cost prices for inventory items in totes is
 * per tonne, and when ordered, 1 tote should reflect half that cost price"):
 * an item in totes picked on an order or PO line starts on its tote ("500kg
 * tote", or the size its name says — utils/itemCostUnit.defaultLineWeightKg),
 * not the item's tonne, and a PO line's cost is the item's per that tote.
 *
 * 2.22.0 (2026-10-28, Cam — "when item is changed via the item picker please
 * replace the sales/ purchase description with the newly selected item's"):
 * a DIFFERENT item picked on a line (or the line's first) replaces its words
 * with the new item's — typed or not — by document (utils/pickedItemWords): an
 * order line's Sale box its Sales Description and its Purchase box its
 * Purchase Description (each else its name); an invoice line the Sales
 * Description; a PO or bill line the Purchase Description. The same item
 * picked again leaves the words alone. After delivery (`closed`) the words
 * follow the corrected item the same way.
 *
 * 2.25.0 (2026-10-30, Cam — the Item picker's X): `onRemovePrevious` is handed
 * to every line's picker, which then puts an X on each "Recently ordered" /
 * "Recently bought" row (hooks/usePreviousItemRemoval does the rest).
 *
 * 2.27.0 (2026-11-02, Cam — the item's supplier): on a customer order and an
 * invoice (a line's own Supplier), picking an item whose supplier is ONE
 * contact fills the line's supplier only when it has none, and that supplier
 * is pinned at the top of the line's list (sticky — in view while the rest
 * scrolls). An item from a supplier GROUP fills nothing: the group's depots
 * are pinned and the person picks one ("always make users pick a supplier for
 * any MVFS supplier group member"). Contracts still fill no supplier (Cam,
 * 2026-09-29). Rules utils/itemSupplier.
 *
 * 2.28.0 (2026-11-03, Cam — "Typed lines should require user to input sales
 * and purchase account codes when orders are created (and invoices or
 * whatever)"): a TYPED line (no inventory item) names its own accounts
 * (utils/lineAccountRequired). On an order line, under its item, a Sales
 * account and a Purchase account (`accounts`, `purchaseAccounts`; the line's
 * line_sales_account_code / line_purchase_account_code); on a PO line a
 * Purchase account (its account_code; fixed on a line bought for an order
 * line — the order's). With `requireAccounts`, one that is required and
 * missing — the sales account always, the purchase account when the line is
 * bought (a supplier, not stock) — is outlined in the error colour with the
 * reason, as is an invoice line's Account cell. `accountsUnavailable` greys
 * the order line's pickers with why (before the migration). Item lines show
 * nothing new.
 */

import React, { useEffect, useMemo, useState } from 'react';
import Input from './Input';
import Textarea from './Textarea';
import SearchableDropdown from './SearchableDropdown';
import AdditiveMultiPicker from './AdditiveMultiPicker';
import OrderItemPicker from './OrderItemPicker';
import { allowsAdditives, getAccountName } from '../utils/accountCodes';
import { getTaxRate, getTaxTypeLabel, SALES_TAX_OPTIONS, PURCHASE_TAX_OPTIONS } from '../utils/taxTypes';
import { itemCodeOf, itemNameOf } from '../utils/customerPricing';
import { wordsOnItemPick } from '../utils/pickedItemWords';
import { resolveItemForVariant, newLineId, round2 } from '../utils/lineItems';
import { formatCurrency, formatPrice } from '../utils/currencyFormatter';
import { perUnitOf, perTonne } from '../utils/unitPriceLabel';
import { isChargeBillLine } from '../utils/chargeLines';
import { formatDecimalInput, formatCount, formatCountInput, formatQuantity } from '../utils/numberFormatters';
import './OrderLinesGrid.css';
import { qtyPerPalletLabel, OTHER_UNIT, unitOptionsFor, unitValueFor, weightKgOf, unitSuffixFor } from '../utils/units';
import { NO_WEIGHT_UNIT, NO_WEIGHT_LABEL, isNoWeight, lineWeightKg, noWeightUnits } from '../utils/lineUnits';
import { quantityWithTonnes } from '../utils/qtyWithTonnes';
import TransportStack from './TransportStack';
import Dropdown from './Dropdown';
import TickCheckbox from './TickCheckbox';
import { itemCostForLine, needsCostUnit, scaleForUnitChange, costUnitChoices, costMoney, perUnitWords, defaultLineWeightKg } from '../utils/itemCostUnit';
import { explicitUnitKg, lineUnitKnown, UNIT_PROBLEM_TEXT } from '../utils/lineUnitRequired';
import { isTypedLine, orderLineAccountsMissing, accountCodeOf, LINE_SALES_ACCOUNT_FIELD, LINE_PURCHASE_ACCOUNT_FIELD, SALES_ACCOUNT_PROBLEM_TEXT, PURCHASE_ACCOUNT_PROBLEM_TEXT, DOCUMENT_ACCOUNT_PROBLEM_TEXT } from '../utils/lineAccountRequired';
import FieldChoice from './FieldChoice';
import { withStoreTag, isStockSupplier } from '../utils/stockSupplier';
import { lineSupplierFill, pinnedSupplierIds, pinSupplierOptions } from '../utils/itemSupplier';
import { API_URL } from '../config';

/** Columns and rules per document. See the header for why they differ. */
const VARIANTS = {
  order: {
    columns: ['item', 'desc', 'additives', 'supplier', 'qty', 'units', 'weight', 'pallets', 'cost', 'price', 'tax', 'taxAmount', 'total'],
    priceLabel: 'Unit Sale',
    taxOptions: SALES_TAX_OPTIONS,
    party: 'customer',
  },
  invoice: {
    // Units: the item's, a unit of the line's own, or None — no weight (Cam,
    // 2026-10-12). No Weight or Pallets: an invoice carries neither.
    columns: ['item', 'desc', 'additives', 'supplier', 'qty', 'units', 'price', 'account', 'tax', 'taxAmount', 'total'],
    priceLabel: 'Unit Price',
    taxOptions: SALES_TAX_OPTIONS,
    party: 'customer',
  },
  purchase: {
    columns: ['item', 'desc', 'additives', 'transport', 'qty', 'units', 'weight', 'pallets', 'price', 'tax', 'taxAmount', 'total'],
    priceLabel: 'Unit Cost',
    taxOptions: PURCHASE_TAX_OPTIONS,
    party: 'supplier',
  },
  bill: {
    // Tax is shown under the line total, and Additives only when a line can
    // take them (2026-10-16): a bill is entered beside its PDF, and the item
    // and words need the room.
    columns: ['item', 'desc', 'additives', 'po', 'qty', 'price', 'account', 'tax', 'total'],
    compact: true,
    priceLabel: 'Unit Price',
    taxOptions: PURCHASE_TAX_OPTIONS,
    party: 'supplier',
  },
};

/** A blank row. Everything the save payload can carry, so no field appears later. */
export const blankLine = () => ({
  id: newLineId(),
  isNew: true,
  contract_id: null,
  monthly_pricing_id: null,
  milk_powder_price_id: null,
  inventory_item_id: null,
  product_name: '',
  // The PURCHASE wording on an order line (what its PO and the supplier's bill
  // say); product_name is the SALE wording (the invoice). Order variant only.
  purchase_description: '',
  product_code: '',
  quantity: '',
  unit_price: '',
  cost_price: '',
  tax_type: null,
  tax_rate: 0,
  account_code: '',
  // A typed order line's own accounts (utils/lineAccountRequired); an item
  // line takes its item's.
  line_sales_account_code: '',
  line_purchase_account_code: '',
  additives: [],
  source: 'inventory',
  category_name: null,
  supplier_contact_id: null,
  supplier_name: null,
  supplier_account_no: null,
  haulier: '',
  driver: '',
  vehicle: '',
  price_accepted: false,
  // The line's own units — blank = the item's. See the header.
  unit_weight_kg: '',
  qty_per_pallet: '',
  pallets: '',
  unit_is_other: false,
  pallet_weight_kg: '',
  // What the item's cost is per, when the person said so on this line
  // (utils/itemCostUnit) — screen only, never saved.
  _costBasisKg: null,
  // Supplier haulage on a Direct Delivery order (£/pallet); overridden = typed.
  haulage_rate: '',
  haulage_overridden: false,
});

/** A positive number, or null. */
const pos = (v) => {
  const x = parseFloat(v);
  return Number.isFinite(x) && x > 0 ? x : null;
};

/**
 * A figure that is WORKED OUT from others (a line's weight from its quantity
 * and unit weight), but can be typed over. Holds what is being typed while
 * focused and hands it over only on leaving, because the shown value is
 * re-derived from what the typing sets and would otherwise reformat under the
 * cursor.
 */
function CommitInput({ value, onCommit, ...props }) {
  const [text, setText] = useState(null);
  return (
    <Input
      type="number"
      {...props}
      value={text ?? value}
      onFocus={() => setText(String(value ?? ''))}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        if (text !== null && text !== String(value ?? '')) onCommit(text);
        setText(null);
      }}
    />
  );
}

/**
 * Account options for a picker: the list, badged by code and searchable by
 * code or name, with a code the line already carries kept even when the list
 * does not have it.
 */
const accountOptionsFrom = (list, value, blankLabel) => {
  const opts = [
    // null, not '': a line with no account shows the picker's placeholder
    // (in the error colour while it is required), and choosing this clears it.
    { value: null, label: blankLabel },
    ...(list || []).map(a => ({
      value: a.code,
      badge: a.code,
      label: a.name || getAccountName(a.code) || '',
      searchText: `${a.code} ${a.name || ''}`,
    })),
  ];
  const code = accountCodeOf(value);
  if (code && !opts.some(o => accountCodeOf(o.value) === code)) {
    opts.push({ value: code, badge: code, label: getAccountName(code) || '' });
  }
  return opts;
};

/**
 * A typed line's own account (utils/lineAccountRequired), under its item: a
 * small label over a compact picker, outlined in the error colour when it is
 * required and missing.
 */
function TypedAccountPicker({ label, value, list, onChange, missing, missingText, disabled, title }) {
  return (
    <div className={`typed-account${missing ? ' account-missing' : ''}`} title={title || (missing ? missingText : undefined)}>
      <span className="typed-account-label">{label}</span>
      <SearchableDropdown
        value={accountCodeOf(value) || ''}
        onChange={(v) => onChange(v ? accountCodeOf(v) : '')}
        options={accountOptionsFrom(list, value, `No ${label.toLowerCase()}`)}
        placeholder={missing ? `Choose a ${label.toLowerCase()}` : label}
        placeholderClassName={missing ? 'sd-placeholder-error' : ''}
        searchPlaceholder="Search by code or name..."
        disabled={disabled}
        compact
        {...MENU_ESCAPE}
      />
    </div>
  );
}

/**
 * How every menu in this grid escapes and sizes itself.
 *
 * Without a boundary the positioning hook measures against the nearest scroll
 * ancestor, which here is .order-lines-grid — it carries overflow-x for the
 * horizontal scroll, and is only as tall as the table. The menu then decides
 * there is no room in either direction and clamps to its 120px floor, which is
 * why the supplier menu opened as a stub. Measuring against the page area
 * instead gives it the real space, and escapeOverflow lets it use it.
 */
const MENU_ESCAPE = {
  escapeOverflow: true,
  reflowOnScroll: true,
  boundarySelector: '.content-inner',
};

/**
 * Size a textarea to its content. CSS field-sizing:content does this natively
 * where it is supported; this is the fallback, and costs one layout read.
 */
const autoGrow = (el) => {
  if (!el || el.tagName !== 'TEXTAREA') return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};

// Used through an INLINE ref (see the Description cell): a new function each
// render makes React re-run it on every commit, so a description filled in by
// picking an item, or loaded with an existing order, is sized straight away —
// not only once someone types in it. A stable ref would run on mount alone.
const growDescription = (td) => {
  // Every textarea in the cell — an order line has two (sale and purchase).
  if (td) td.querySelectorAll('textarea').forEach(autoGrow);
};

/*
 * "Looks like a customer's name" under a purchase description, as the item
 * form warns (POST /api/inventory/text-check). Advisory, never blocking, and
 * debounced so it asks once typing pauses. Customer findings only: the check's
 * PR-number finding fires on "(PR-11204)", which is exactly what belongs here.
 * Answers are cached per text for the session.
 */
const textCheckCache = new Map();
function usePurchaseTextWarning(text, enabled) {
  const [warning, setWarning] = useState(null);
  useEffect(() => {
    const value = String(text || '').trim();
    if (!enabled || !value) { setWarning(null); return undefined; }
    if (textCheckCache.has(value)) { setWarning(textCheckCache.get(value)); return undefined; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const r = await fetch(`${API_URL}/api/inventory/text-check`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ purchase_description: value })
        });
        const d = await r.json();
        const found = (d.findings || []).filter(f => f.kind === 'customer');
        const message = found.length
          ? `This may name a customer — ${found.map(f => `"${f.text}" ${f.detail}`).join('; ')}. The supplier sees this wording.`
          : null;
        textCheckCache.set(value, message);
        if (!cancelled) setWarning(message);
      } catch (err) {
        // A check that cannot run says nothing.
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [text, enabled]);
  return warning;
}

/**
 * An order line's two wordings, stacked: Sale (goes on the invoice) over
 * Purchase (goes on the PO and the supplier's bill). Both are textareas so they
 * wrap, but each VALUE stays one line — a line break would split a description
 * in two on the PO, the invoice and in Xero — so Enter is swallowed and pasted
 * newlines become spaces.
 */
function DescriptionPair({ line, disabled, fromStock, onSale, onPurchase }) {
  const purchaseOff = disabled || fromStock;
  const warning = usePurchaseTextWarning(line.purchase_description, !purchaseOff);
  const oneLine = (e) => e.target.value.replace(/\r?\n/g, ' ');
  const noEnter = (e) => { if (e.key === 'Enter') e.preventDefault(); };
  return (
    <div className="desc-pair">
      <div className="desc-pair-row" title="Sale description: goes on the invoice">
        <span className="material-symbols-rounded desc-pair-icon" aria-hidden="true">sell</span>
        <Textarea
          rows={1}
          value={line.product_name || ''}
          onChange={(e) => onSale(oneLine(e))}
          onKeyDown={noEnter}
          onInput={(e) => autoGrow(e.target)}
          placeholder="Sale description"
          aria-label="Sale description"
          disabled={disabled}
        />
      </div>
      <div
        className={`desc-pair-row${fromStock ? ' is-stock' : ''}`}
        title={fromStock
          ? 'From our stock: nothing is bought for this line'
          : "Purchase description: goes on the purchase order and the supplier's bill"}
      >
        <span className="material-symbols-rounded desc-pair-icon" aria-hidden="true">shopping_cart</span>
        <Textarea
          rows={1}
          value={line.purchase_description || ''}
          onChange={(e) => onPurchase(oneLine(e))}
          onKeyDown={noEnter}
          onInput={(e) => autoGrow(e.target)}
          placeholder={fromStock ? 'From our stock' : 'Purchase description'}
          aria-label="Purchase description"
          disabled={purchaseOff}
        />
      </div>
      {warning && <span className="desc-pair-warning">{warning}</span>}
    </div>
  );
}

const n = (v) => {
  const x = parseFloat(v);
  return Number.isFinite(x) ? x : 0;
};

/**
 * A Qty, Unit Cost or Unit Price cell.
 *
 * Out of focus it shows the figure to the system's 2-to-4dp rule — 236 reads
 * "236.00", 236.1250 reads "236.125" — however the line came by it: a saved
 * line arrives as "236.0000", a picked item as 236, a typed one as "236".
 * In focus it shows exactly what is being typed, and nothing is reformatted
 * under the cursor.
 *
 * Leaving a cell that was TYPED in writes the tidied figure back to the line,
 * so what is saved reads the same as the cell. A cell that was only tabbed
 * through writes nothing: that is not a change, and must not clear a "read
 * from the PDF" highlight. Every reader of these fields — lineTotals, the
 * payload mappers in utils/lineItems, the order save — parses them with
 * parseFloat or Number, and typing already hands them strings.
 */
function DecimalInput({ value, onValueChange, format = formatDecimalInput, ...props }) {
  // null out of focus. In focus: the text shown, the line value that text
  // stands for, and whether anything was typed.
  const [editing, setEditing] = useState(null);
  // If the line changes underneath a focused cell, show the line.
  const shown = editing && editing.base === value ? editing.text : format(value);
  return (
    <Input
      type="number"
      {...props}
      value={shown}
      onFocus={() => setEditing({ text: format(value), base: value, typed: false })}
      onChange={(e) => {
        const text = e.target.value;
        setEditing({ text, base: text, typed: true });
        onValueChange(text);
      }}
      onBlur={() => {
        if (editing?.typed) {
          const tidy = format(value);
          if (tidy !== String(value ?? '')) onValueChange(tidy);
        }
        setEditing(null);
      }}
    />
  );
}

/**
 * "Haulage: [18.50] /pallet × 2 = £37.00" under a Direct Delivery line's
 * supplier: what the supplier charges to deliver it, by the customer's
 * postcode, editable. The PO carries it as its own line; pricing folds it into
 * the sale price. A part pallet counts as a whole one, and with no pallet
 * figure there is nothing to multiply — said so rather than guessed.
 */
function LineHaulage({ line, haulage, perPallet, disabled, onRate, onReset, noWeight = false }) {
  const found = haulage.rates?.[line.supplier_contact_id] || null;
  const rate = line.haulage_rate === '' || line.haulage_rate == null ? null : Number(line.haulage_rate);
  const qty = parseFloat(line.quantity);
  const rawPallets = pos(line.pallets) ?? (perPallet && qty > 0 ? qty / perPallet : null);
  const pallets = rawPallets ? Math.ceil(Math.round(rawPallets * 100) / 100) : null;

  // None — no weight, so no pallets and nothing to charge haulage on; the
  // server gives such a line no rate (supplierHaulageService.stampLines).
  if (noWeight) {
    return (
      <div className="line-haulage" title="Units is None: no pallets, so no haulage is charged on this line">
        <span className="line-haulage-label">Haulage:</span>
        <span className="line-haulage-total">none — no weight</span>
      </div>
    );
  }

  let source;
  if (line.haulage_overridden) source = found ? `Typed over the ${found.prefix} rate of £${Number(found.rate).toFixed(2)}` : 'Typed by hand';
  else if (found) source = `${found.prefix} rate from ${String(found.effective_from).slice(0, 10)}, for ${haulage.postcode}`;
  else if (haulage.loading) source = 'Looking up the rate…';
  else if (haulage.error) source = 'The rate could not be looked up — type it';
  else if (!haulage.recognised) source = `No UK postcode on the customer${haulage.postcode ? ` (${haulage.postcode})` : ''} — type the rate`;
  else source = `No rate on file from this supplier for ${haulage.postcode} — type it, or add one on the supplier's Haulage rates`;

  return (
    <div className="line-haulage" title={source}>
      <span className="line-haulage-label">Haulage:</span>
      <DecimalInput
        min="0"
        step="0.01"
        value={line.haulage_rate ?? ''}
        onValueChange={onRate}
        placeholder="0.00"
        disabled={disabled}
        aria-label="Haulage per pallet"
      />
      <span>/pallet</span>
      {rate != null && (pallets
        ? <span className="line-haulage-total">× {pallets} = {formatCurrency(rate * pallets)}</span>
        : <span className="line-haulage-warn" title="Set the Pallets for this line to charge haulage on it">set pallets</span>)}
      {line.haulage_overridden && found && !disabled && (
        <button type="button" className="line-haulage-reset" onClick={onReset} title={`Back to the ${found.prefix} rate`}>
          <span className="material-symbols-rounded">undo</span>
        </button>
      )}
    </div>
  );
}

/**
 * Net, tax and gross for one line. The single place these are worked out.
 *
 * `amountsAre` is the invoice's "Amounts Are" setting, and changes what the
 * typed price MEANS: exclusive of tax (the usual), inclusive of it (tax is
 * backed out of qty × price), or not taxed at all. The server makes the same
 * split when it totals an invoice, so the screen and the saved figures agree.
 */
export function lineTotals(line, amountsAre = 'Exclusive') {
  // A PO line with a supplied quantity is costed on what was supplied (Cam,
  // 2026-10-12), as the server stores it — only PO lines ever carry one.
  const qty = line.supplied_quantity != null && line.supplied_quantity !== '' ? line.supplied_quantity : line.quantity;
  const entered = n(qty) * n(line.unit_price);
  const rate = line.tax_rate != null ? n(line.tax_rate) : (getTaxRate(line.tax_type) || 0);
  if (amountsAre === 'NoTax') return { net: entered, tax: 0, gross: entered };
  if (amountsAre === 'Inclusive') {
    const tax = rate > 0 ? entered - entered / (1 + rate) : 0;
    return { net: entered - tax, tax, gross: entered };
  }
  // A bill line split over several PO lines may carry its own VAT, so the
  // lines' VAT adds up to the invoice line's to the penny
  // (utils/billLineSplit) — only while its amount and tax type are as split.
  const own = line._vat;
  const tax = own && Math.abs(Math.round(entered * 100) / 100 - n(own.net)) < 0.005
    && (own.tax_type || null) === (line.tax_type || null)
    ? n(own.tax)
    : entered * rate;
  return { net: entered, tax, gross: entered + tax };
}

/** A line is real if it names something — an item, or a description. */
export const lineIsEmpty = (line) =>
  !line.inventory_item_id && !String(line.product_name || '').trim();

/**
 * Subtotal, VAT and total under the grid, against the columns they total.
 * Shared by every document so the four read identically.
 *
 * `perLine` (a bill, 2026-10-16): each line's net and VAT rounded to the
 * penny before they are added — what the bill saves and how Xero works an
 * Exclusive bill — so the panel shows the VAT that will be saved (Burcombe's
 * QA03476: 32 lines' VAT £259.60 line by line, £259.61 on the total).
 */
export function LineTotalsPanel({ lines = [], amountsAre = 'Exclusive', perLine = false }) {
  const totals = lines.reduce((acc, line) => {
    const t = lineTotals(line, amountsAre);
    if (perLine) {
      // As billLinesToPayload rounds them (utils/lineItems.round2).
      const net = round2(t.net);
      const tax = round2(t.tax);
      return { net: acc.net + net, tax: acc.tax + tax, gross: acc.gross + net + tax };
    }
    return { net: acc.net + t.net, tax: acc.tax + t.tax, gross: acc.gross + t.gross };
  }, { net: 0, tax: 0, gross: 0 });
  return (
    <div className="order-totals-panel">
      <div className="total-line">
        <span className="total-label">Subtotal</span>
        <span className="total-value">{formatCurrency(totals.net)}</span>
      </div>
      <div className="total-line">
        <span className="total-label">VAT</span>
        <span className="total-value">{formatCurrency(totals.tax)}</span>
      </div>
      <div className="total-line grand">
        <span className="total-label">Total</span>
        <span className="total-value">{formatCurrency(totals.gross)}</span>
      </div>
    </div>
  );
}

/** A tax dropdown's options, keeping a saved type that the short list lacks. */
const taxOptionsWith = (options, current) => (
  !current || options.some(o => o.value === current)
    ? options
    : [...options, { value: current, label: getTaxTypeLabel(current) }]
);

function OrderLinesGrid({
  variant = 'order',
  lines,
  onChange,
  inventoryItems = [],
  // Items this customer (or, on a supplier document, this supplier) has
  // traded before, most recent first — narrows the Item picker's list when its
  // "previous items" toggle is on.
  previousItems = [],
  // (row, item) => take a row off that list for everyone — the screen's
  // hooks/usePreviousItemRemoval. Without it the picker's rows have no X.
  onRemovePrevious = null,
  contracts = [],
  spotPrices = [],
  milkPowderContracts = [],
  suppliers = [],
  // The date prices are resolved for: the delivery date on an order, the
  // invoice date on an invoice.
  deliveryDate = null,
  periodName = null,
  canViewCosts = true,
  // Direct Delivery supplier haulage: { loading, error, postcode, recognised,
  // rates: { supplierId: { rate, prefix, effective_from } } }, or null when the
  // order is not Direct Delivery (or costs are hidden) and nothing is shown.
  haulage = null,
  lockedLineIds = null,
  // Set of the only fields that may change ('price' is the one in use) — every
  // other cell is read-only and lines can't be added or removed. For a document
  // whose lines ARE another one's: an invoice made from a customer order.
  editableFields = null,
  // (line) => node shown under a line's price input — the price suggestions on
  // an invoice made from an order.
  priceAside = null,
  // (line) => what the line's price and cost are per, as utils/unitPriceLabel
  // facts ({ unitWeightKg, itemUnitWeightKg, accountCode, unit }), or null for
  // the grid's own reading (the line's unit, else its item's) — Add Bill's
  // lines are per what was billed, else their PO line's unit.
  priceUnitFacts = null,
  onAddItem,
  // Called with the picked item and the row, so a host can react — suggesting
  // a header supplier from the item code, say.
  onItemPicked,
  // "Amounts Are" on an invoice. Every other document is tax exclusive.
  amountsAre = 'Exclusive',
  // [{ code, name }] for the Account column.
  accounts = [],
  // The header supplier, on a supplier document — what additives are scoped to.
  supplierAccountId = null,
  // Transport, on a purchase order: haulier names, driver records (with their
  // hauliers and default vehicle) and vehicle registrations.
  hauliers = [],
  drivers = [],
  vehicles = [],
  // Why the Transport cell cannot be changed here, when it cannot (a PO for
  // today or later is planned on the Transport Planning board only).
  transportLockedReason = null,
  // On a Customer Collection PO: what the Transport cell reads ("Customer
  // Collection"), fixed — the customer collects, so there is nothing to set.
  transportFixedLabel = null,
  // Lines on purchase orders this bill is linked to — shown in the Item
  // picker's side panel. [{ product_code, product_name, quantity }]
  linkedLines = [],
  disabled = false,
  // Why the grid is inert, when it is. Shown on the cells and on Add line, so
  // the answer is wherever the user's pointer already is rather than somewhere
  // else on the page.
  disabledHint = null,
  // Rendered opposite "Add line", on the same band directly under the table.
  // The totals go here rather than in the footer below, so the figure sits
  // against the column it totals instead of a third of the page away.
  footerRight = null,
  // On a bill: [{ value: poId, label: 'PO-D10287' }] — the bill's POs, for a
  // PO column saying which one each line bills. null hides the column.
  poOptions = null,
  // { [line.id]: { tone: 'error'|'query', icon?, text } } — a flag shown on a
  // line, for something the user must act on before saving (Add Bill: a line
  // not reconciled with its PO, a quantity to query with the supplier).
  lineFlags = null,
  // After delivery (or invoicing): { fields: Set, poLineFields?: Set, reason }
  // — `fields` are closed on every line ('qty', 'cost', 'price', 'tax',
  // 'additives', 'add', 'remove'), `poLineFields` only on a line bought on a PO (in
  // lockedLineIds). Each closed cell is greyed with `reason` as its title.
  // `unitsBlock(line)` (2.26.0): why that line's units cannot be corrected.
  closed = null,
  // Customer orders (Cam, 2026-10-28): a line with no known unit has its
  // Units cell outlined, with the reason (utils/lineUnitRequired).
  requireUnits = false,
  // Typed lines name their accounts (Cam, 2026-11-03 — utils/lineAccountRequired):
  // a required account a typed line lacks is outlined, with the reason. true =
  // every typed line; a function (line) → boolean = only the lines it names
  // (the host's new and changed lines, as the save refuses them).
  requireAccounts = false,
  // [{ code, name }] for an order line's Purchase account (its `accounts` are
  // the sales accounts).
  purchaseAccounts = [],
  // Why an order line's own accounts cannot be kept yet (before
  // migrations/order_line_account_codes.sql) — its pickers are greyed with it.
  accountsUnavailable = null,
  // Why a line's own unit cannot be stored yet (an invoice before
  // migrations/invoice_line_unit_weight.sql) — the Units cell is greyed with it.
  unitsUnavailable = null,
  // (line) → why this one line cannot be removed, else null — greyed with it.
  removeBlock = null,
  // (line, col) → FieldChoice props for a cell whose PO and bill values
  // differ (Add Bill), else null. col: 'item' | 'desc' | 'qty' | 'price' |
  // 'account' | 'tax'.
  cellChoice = null,
  // (line, index) → a heading row shown above the line, else null.
  groupHeader = null,
  // (line) → the price cell's placeholder for this line, else null ("0.00").
  pricePlaceholder = null,
}) {
  const config = VARIANTS[variant] || VARIANTS.order;
  // A compact grid (a bill) shows Additives only when some line has them or
  // can take them (a compound's item).
  const additivesInUse = !config.compact || lines.some(l => (l.additives || []).length
    || allowsAdditives(inventoryItems.find(i => i.id === l.inventory_item_id)?.sales_account_code));
  const columns = config.columns.filter(c => (c !== 'cost' || canViewCosts) && (c !== 'po' || Array.isArray(poOptions))
    && (c !== 'additives' || additivesInUse));
  const has = (col) => columns.includes(col);
  const isSupplierDoc = config.party === 'supplier';

  /*
   * The line's COST cell: an order's Unit Cost, a PO's Unit Cost (its price
   * column). Its value is per the line's unit (utils/itemCostUnit).
   */
  const costField = variant === 'order' ? 'cost_price' : (variant === 'purchase' ? 'unit_price' : null);
  const costShown = variant === 'order' ? has('cost') : variant === 'purchase' ? has('price') : false;
  // Item units saved from this grid ("Save on the item for next time"), laid
  // over the list the host passed until it reloads.
  const [savedUnits, setSavedUnits] = useState({});
  const [costUnitError, setCostUnitError] = useState({});
  const itemFor = (line) => {
    const it = line?.inventory_item_id ? inventoryItems.find(i => i.id === line.inventory_item_id) : null;
    if (!it) return null;
    return savedUnits[it.id] ? { ...it, unit_weight_kg: savedUnits[it.id] } : it;
  };
  // The item's own cost: /api/orders/inventory-items says item_cost (costs.view
  // only), the PO editor's list purchase_unit_price.
  const itemCostOf = (item) => item?.item_cost ?? item?.purchase_unit_price ?? null;

  const supplierOptions = useMemo(() => ([
    { value: null, label: 'Supplier' },
    ...suppliers.map(s => withStoreTag(s, {
      value: s.id,
      label: s.company_name,
      // The number is gone from the label but kept here, so someone who knows
      // the account by number can still type it.
      searchText: `${s.account_no || ''} ${s.company_name || ''}`,
    })),
  ]), [suppliers]);

  // Badged like item codes, so a code reads the same wherever it appears. The
  // name is searchable too: nobody remembers that fuel is 449.
  const accountOptions = useMemo(() => ([
    { value: '', label: 'Account' },
    ...accounts.map(a => ({
      value: a.code,
      badge: a.code,
      label: a.name || getAccountName(a.code) || '',
      searchText: `${a.code} ${a.name || ''}`,
    })),
  ]), [accounts]);

  const patch = (index, changes) => {
    const next = [...lines];
    // The "read from the PDF" highlight marks a row nobody has checked yet.
    // Touching it is checking it.
    next[index] = { ...next[index], _highlight: false, ...changes };
    onChange(next);
  };

  /*
   * A change to a line's units (Units, Weight, "Other"): its cost per unit
   * follows, so the cost per tonne stays the same — 40 × 25kg bag at £11.60
   * becomes 1 T at £464.00 (utils/itemCostUnit.scaleForUnitChange). Unknown
   * or None either side: the cost is left as it is.
   */
  const patchUnits = (index, changes) => {
    const line = lines[index];
    let next = changes;
    // After delivery (closed) the host asks what the price was per and moves
    // the money itself (utils/unitsAfterDelivery) — never re-costed here.
    if (costField && costShown && line && !closed) {
      const iw = itemFor(line)?.unit_weight_kg;
      const nextWeight = 'unit_weight_kg' in changes ? changes.unit_weight_kg : line.unit_weight_kg;
      const scaled = scaleForUnitChange(line[costField], lineWeightKg(line.unit_weight_kg, iw), lineWeightKg(nextWeight, iw));
      if (scaled !== null) next = { ...changes, [costField]: String(scaled) };
    }
    patch(index, next);
  };

  /*
   * The answer to "what is this item's cost per?" (utils/itemCostUnit). The
   * line is costed in its own unit from it; ticked, the unit is saved on the
   * item first (PATCH /api/inventory/:id/unit-weight) so the next order needs
   * no question — and an order line's cost can then stay blank (derived, the
   * server converting it the same way). Not ticked, or the save refused: the
   * cost is written on this line, and a line with no unit takes the one
   * chosen.
   */
  const answerCostUnit = async (index) => {
    const line = lines[index];
    const item = itemFor(line);
    const kg = Number(line?._costUnitChoice);
    if (!item || !(kg > 0)) return;
    let saved = false;
    if (line._costUnitSave !== false) {
      try {
        const r = await fetch(`${API_URL}/api/inventory/${item.id}/unit-weight`, {
          method: 'PATCH', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ unit_weight_kg: kg })
        });
        const d = await r.json().catch(() => ({}));
        if (r.ok) {
          saved = true;
          setSavedUnits(m => ({ ...m, [item.id]: kg }));
          setCostUnitError(m => ({ ...m, [line.id]: null }));
        } else {
          setCostUnitError(m => ({ ...m, [line.id]: d.error || 'The unit could not be saved on the item' }));
        }
      } catch (err) {
        setCostUnitError(m => ({ ...m, [line.id]: 'The unit could not be saved on the item' }));
      }
    }
    const lineHasUnit = lineWeightKg(line.unit_weight_kg, item.unit_weight_kg) !== null;
    const r = itemCostForLine(item, { cost: itemCostOf(item), lineWeight: lineHasUnit ? line.unit_weight_kg : '', basisKg: kg });
    const changes = { _costBasisKg: kg, _costUnitChoice: '' };
    if (!saved && !lineHasUnit) changes.unit_weight_kg = String(kg);
    // A PO line's cost is always its own; an order line's stays derived when
    // the item now says what its cost is per.
    if (variant === 'purchase' || !saved) changes[costField] = r.cost === null ? '' : String(r.cost);
    patch(index, changes);
  };

  const removeLine = (index) => onChange(lines.filter((_, i) => i !== index));

  const addLine = () => onChange([...lines, blankLine()]);

  const handleItemSelect = (index, item, { contractKey = null } = {}) => {
    if (!item) {
      // "No item" — keep whatever description has been typed and drop the link
      // to inventory. This is what makes the line a typed one-off.
      patch(index, {
        inventory_item_id: null,
        product_code: '',
        contract_id: null,
        monthly_pricing_id: null,
        milk_powder_price_id: null,
        category_name: null,
        source: 'inventory',
        _hint: null,
      });
      return;
    }
    const current = lines[index];
    const resolved = resolveItemForVariant(variant, item, {
      contracts, spotPrices, milkPowderContracts, suppliers, deliveryDate, periodName,
      // The line's own additives pick the matching contract version — none
      // picks the plain one — unless a particular contract was chosen.
      additives: current.additives || [],
      contractKey,
    });
    // The resolvers' own wordings are not the line's: the words come from
    // utils/pickedItemWords below, by document.
    const { sales_text: _salesText, purchase_text: _purchaseText, product_name: _itemName, ...resolvedLine } = resolved;
    // An item in totes is costed per tonne and starts on its tote (Cam, 23
    // Oct 2026 — "1 tote should reflect half that cost price"): the line's
    // unit is the tote, and a PO line's cost is the item's per tote.
    const startKg = variant === 'order' || variant === 'purchase' ? defaultLineWeightKg(item) : null;
    const startCost = variant === 'purchase' && startKg && resolvedLine.unit_price !== '' && resolvedLine.unit_price != null
      ? itemCostForLine(item, { cost: resolvedLine.unit_price, lineWeight: String(startKg) }).cost
      : null;
    const changes = {
      ...resolvedLine,
      ...(startCost !== null ? { unit_price: String(startCost) } : {}),
      // A PO line's cost from an item whose cost is per nobody knows what is
      // not filled in — the line asks (utils/itemCostUnit).
      ...(variant === 'purchase' && needsCostUnit(item, itemCostOf(item)) ? { unit_price: '' } : {}),
      _costBasisKg: null,
      // A different item brings its own units: a line's overrides were about
      // the item it had (a tote item: its tote). On an order or PO line the
      // item's unit is put ON the line (Cam, 2026-10-28 — units are explicit).
      unit_weight_kg: (() => {
        const kg = variant === 'order' || variant === 'purchase' ? explicitUnitKg(item) : startKg;
        return kg ? String(kg) : '';
      })(),
      qty_per_pallet: '',
      pallets: '',
      unit_is_other: false,
      pallet_weight_kg: '',
      // A different item brings its own words, typed ones included (Cam,
      // 2026-10-28): an order's Sale and Purchase boxes, an invoice line's sale
      // words, a PO or bill line's purchase words — utils/pickedItemWords. A
      // PO must never carry one product's words beside another's code. The
      // same item picked again leaves the line's words as they are.
      ...wordsOnItemPick(variant, current, item),
      // The price the item brought, so a later additive change knows whether the
      // price is still ours to change or has been typed over.
      _autoPrice: resolved.unit_price,
      _hint: null,
      // A contract's recipe only rides along where the account allows additives
      // at all — compounds. Anywhere else it would be invisible on the line but
      // still reach the supplier on the purchase order.
      additives: allowsAdditives(item.sales_account_code) ? (resolved.additives || []) : [],
    };
    // A supplier document's supplier is the header's, and a customer line's
    // own supplier is the user's choice: an item brings none (nothing is filled
    // in automatically), so picking one never clears a supplier already chosen.
    if (isSupplierDoc || !changes.supplier_contact_id) {
      delete changes.supplier_contact_id;
      delete changes.supplier_name;
      delete changes.supplier_account_no;
    }
    // The item's own supplier (Cam, 2026-11-02): ONE contact fills a line
    // that has none — never over one chosen or loaded, never after delivery
    // (a correction keeps the line's supplier), never from a supplier group
    // (its depots are pinned in the list; the person picks one).
    if (!isSupplierDoc && !closed && has('supplier')) {
      const fill = lineSupplierFill(item, { current: current.supplier_contact_id, offered: suppliers });
      if (fill) {
        const s = suppliers.find(x => Number(x.id) === fill) || null;
        Object.assign(changes, {
          supplier_contact_id: s ? s.id : fill,
          supplier_name: s?.company_name ?? null,
          supplier_account_no: s?.account_no ?? null,
          ...(haulage ? {
            haulage_rate: haulage.rates?.[s ? s.id : fill] ? Number(haulage.rates[s ? s.id : fill].rate).toFixed(2) : '',
            haulage_overridden: false,
          } : {}),
        });
      }
    }
    // An account already chosen by hand is not overwritten by an item that has
    // none of its own.
    if (!changes.account_code && current.account_code) delete changes.account_code;
    // After delivery (Cam, 2026-10-09): a corrected item keeps the line's
    // quantity, price, cost, tax, additives, units, supplier and contract — only
    // what names the product moves (its code and words).
    if (closed) {
      const keep = ['inventory_item_id', 'product_code', 'product_name', 'purchase_description', 'item_code', 'item_name', 'account_code'];
      const only = Object.fromEntries(Object.entries(changes).filter(([k]) => keep.includes(k)));
      const linked = !!(current.contract_id || current.monthly_pricing_id);
      patch(index, {
        ...only,
        _hint: linked
          ? 'Item corrected — quantity, price and cost kept; its contract link goes when you save (it was the other item\'s)'
          : 'Item corrected — quantity, price and cost kept as they were'
      });
      onItemPicked?.(item, index);
      return;
    }
    patch(index, changes);
    onItemPicked?.(item, index);
  };

  /*
   * Additives changed on a line. Where the customer holds a contract for this
   * product with exactly these additives — the same feed plain and with C16 2%
   * are two contracts at two prices — the line moves onto it. The price follows
   * only if nobody has typed over the one the item brought.
   */
  const handleAdditivesChange = (index, next) => {
    const line = lines[index];
    const changes = { additives: next };
    const item = line.inventory_item_id ? inventoryItems.find(i => i.id === line.inventory_item_id) : null;
    if (item && Array.isArray(contracts) && contracts.length && config.party === 'customer') {
      const resolved = resolveItemForVariant(variant, item, {
        contracts, spotPrices, milkPowderContracts, suppliers, deliveryDate, periodName, additives: next,
      });
      const moved = resolved && (resolved.contract_id !== line.contract_id
        || resolved.monthly_pricing_id !== line.monthly_pricing_id);
      if (moved && resolved.source === 'contract') {
        Object.assign(changes, {
          contract_id: resolved.contract_id,
          monthly_pricing_id: resolved.monthly_pricing_id,
          milk_powder_price_id: null,
          category_name: resolved.category_name,
          source: resolved.source,
          contract_number: resolved.contract_number ?? null,
          supplier_contract_number: resolved.supplier_contract_number ?? null,
          contracted_quantity: resolved.contracted_quantity ?? null,
          ordered_quantity: resolved.ordered_quantity ?? null,
        });
        const priceUntouched = line._autoPrice == null || Number(line.unit_price) === Number(line._autoPrice);
        if (priceUntouched) {
          changes.unit_price = resolved.unit_price;
          changes._autoPrice = resolved.unit_price;
          if (has('cost')) changes.cost_price = resolved.cost_price;
        }
      }
    }
    patch(index, changes);
  };

  const isLocked = (line) => !!lockedLineIds && lockedLineIds.has(line.id);

  // A cell the PO and the bill disagree on: offered both, in the cell itself.
  // A picker opens the card from its marker (its own menu opens on a click).
  const withChoice = (line, col, node, { picker = false } = {}) => {
    const c = typeof cellChoice === 'function' ? cellChoice(line, col) : null;
    if (!c) return node;
    return <FieldChoice {...c} openOnFocus={!picker && c.openOnFocus !== false}>{node}</FieldChoice>;
  };
  const flagsOf = (line) => {
    const f = lineFlags ? lineFlags[line.id] : null;
    if (!f) return [];
    return (Array.isArray(f) ? f : [f]).filter(Boolean);
  };

  return (
    <div className={`order-lines-grid variant-${variant}${lines.some(l => l.unit_is_other) ? ' has-other-units' : ''}`}>
      <table className="line-items-table order-grid">
        <thead>
          <tr>
            {has('item') && <th className="col-item">Item</th>}
            {has('desc') && <th className="col-desc">Description</th>}
            {has('additives') && <th className="col-additives">Additives</th>}
            {has('supplier') && <th className="col-supplier">Supplier</th>}
            {has('transport') && <th className="col-transport">Transport</th>}
            {has('po') && <th className="col-po" title="Which of this bill's purchase orders the line bills">PO</th>}
            {has('qty') && <th className="col-num">Qty</th>}
            {has('units') && <th className="col-units">Units</th>}
            {has('weight') && <th className="col-num" title="Quantity × weight per unit — type to set it for this line">Weight (T)</th>}
            {has('pallets') && <th className="col-num col-pallets" title="Quantity ÷ qty per pallet — type to set it for this line">Pallets</th>}
            {has('cost') && <th className="col-num">Unit Cost</th>}
            {has('price') && <th className="col-num">{config.priceLabel}</th>}
            {has('account') && <th className="col-account">Account</th>}
            {has('tax') && <th className="col-tax">Tax Type</th>}
            {has('taxAmount') && <th className="col-num is-derived">Tax</th>}
            {has('total') && <th className="col-num is-derived col-total">Line Total</th>}
            <th className="col-actions" />
          </tr>
        </thead>
        <tbody>
          {lines.length === 0 && (
            <tr className="order-grid-empty">
              <td colSpan={columns.length + 1}>
                {disabledHint || 'No lines yet — add one below.'}
              </td>
            </tr>
          )}

          {lines.map((line, index) => {
            const { tax, gross } = lineTotals(line, amountsAre);
            // A line on a PO is marked, not disabled: the edit is allowed and
            // the PO is rebuilt on save (see CreateCustomerOrder). Disabling it
            // here contradicted both that and the CSS for this row state.
            const locked = isLocked(line);
            const rowDisabled = disabled || !!editableFields;
            const isClosed = (f) => !!closed && (closed.fields?.has(f) || (locked && !!closed.poLineFields?.has(f)));
            const fieldOff = (f) => disabled || (!!editableFields && !editableFields.has(f)) || isClosed(f);
            // The cell's own title when it is closed after delivery.
            const closedTitle = (f) => (isClosed(f) ? closed.reason : undefined);
            // After delivery: why this line's units cannot be corrected, if not.
            const unitsClosed = closed && typeof closed.unitsBlock === 'function' ? (closed.unitsBlock(line) || null) : null;
            const removeReason = typeof removeBlock === 'function' ? (removeBlock(line) || null) : null;
            const item = itemFor(line);
            // '' for the great majority of items, which show no hint at all.
            // Units: the line's own where set, the item's otherwise.
            const itemKg = weightKgOf(item?.unit_weight_kg);
            // None (no weight — a service or weighbridge charge): 0, never the item's.
            const noWeight = isNoWeight(line.unit_weight_kg);
            const unitKg = lineWeightKg(line.unit_weight_kg, item?.unit_weight_kg);
            // Units required (customer orders): a line with an item or words
            // and no unit known, its own or its item's.
            const unitMissing = requireUnits && !!(line.inventory_item_id || String(line.product_name || '').trim())
              && !lineUnitKnown(line, item);
            // A typed line's own accounts (Cam, 2026-11-03 — utils/lineAccountRequired).
            const typedLine = isTypedLine(line);
            const asksAccounts = typeof requireAccounts === 'function' ? !!requireAccounts(line) : !!requireAccounts;
            const lineSupplier = line.supplier_contact_id ? suppliers.find(x => x.id === line.supplier_contact_id) : null;
            const fromStockSupplier = isStockSupplier(lineSupplier);
            const orderAccountsMissing = variant === 'order' && typedLine && asksAccounts && !accountsUnavailable
              ? orderLineAccountsMissing(line, { bought: l => !!l.supplier_contact_id && !fromStockSupplier })
              : { sales: false, purchase: false };
            // A PO line bought for an order line takes its order line's account.
            const poAccountFixed = variant === 'purchase' && line.orderLineId
              ? 'Bought for a customer order line — its purchase account is set on the order line'
              : null;
            const docAccountMissing = asksAccounts && typedLine && !poAccountFixed
              && (variant === 'invoice' || variant === 'purchase') && !accountCodeOf(line.account_code);
            const perPallet = noWeight ? null : (pos(line.qty_per_pallet) ?? pos(item?.qty_per_pallet));
            // A pack count, or a None line's plain count: only the places it has.
            const countQty = noWeight || unitSuffixFor(unitKg).startsWith('×');
            const qtyNum = n(line.quantity);
            const lineTonnes = noWeight ? 0 : (unitKg && qtyNum ? (qtyNum * unitKg) / 1000 : null);
            const derivedPallets = perPallet && qtyNum ? qtyNum / perPallet : null;
            const palletQty = noWeight ? '' : (perPallet ? qtyPerPalletLabel({ qty_per_pallet: perPallet }) : qtyPerPalletLabel(item));
            // The classification belongs to the item: a compound is milled to
            // order whichever side of the trade the document is on. A typed
            // invoice line has no item, so its own sales account decides.
            const additivesOk = allowsAdditives(
              item?.sales_account_code ?? (variant === 'invoice' ? line.account_code : null)
            );
            const additiveSupplier = isSupplierDoc ? supplierAccountId : line.supplier_contact_id;
            // The item's cost, per this line's unit (utils/itemCostUnit).
            const itemCost = costShown && item ? itemCostForLine(item, {
              cost: itemCostOf(item), lineWeight: line.unit_weight_kg, basisKg: line._costBasisKg
            }) : null;
            const costBlank = costField ? (line[costField] === '' || line[costField] === null || line[costField] === undefined) : false;
            // What this line's price and cost are per (utils/unitPriceLabel).
            const unitFacts = (typeof priceUnitFacts === 'function' && priceUnitFacts(line)) || {
              unitWeightKg: line.unit_weight_kg,
              itemUnitWeightKg: item?.unit_weight_kg,
              accountCode: item?.sales_account_code ?? (variant === 'invoice' ? line.account_code : null),
              unit: variant === 'bill' ? line.bill_unit : null
            };
            const perUnit = perUnitOf(unitFacts);
            const lineInUse = !!(line.inventory_item_id || String(line.product_name || '').trim() || String(line.product_code || '').trim());
            // A bill's charge lines (haulage, fuel) are a job's or a pallet's,
            // whatever the reader took the quantity to be: no unit said (as the bill window).
            const chargeLine = variant === 'bill' && isChargeBillLine({
              item_code: line.product_code, account_code: line.account_code, description: line.product_name, po_line_key: line.po_line_key
            });
            const unitHint = (value) => {
              if (!lineInUse || chargeLine) return null;
              if (perUnit.kind === 'unknown') {
                return <span className="price-unit-hint unit-unknown" title={perUnit.title}>unit not set</span>;
              }
              const t = perTonne(value, unitFacts);
              return (
                <span className="price-unit-hint" title={perUnit.title}>
                  <span className="per">{perUnit.short}</span>
                  {t ? <span className="per-tonne" title="The same figure a tonne">= <span className="fig">{formatPrice(t)}/T</span></span> : null}
                </span>
              );
            };
            const askCostUnit = !!itemCost && itemCost.needsUnit && costBlank && !noWeight && !rowDisabled;
            const costUnitFlag = askCostUnit ? {
              key: 'cost-unit', tone: 'query', icon: 'help',
              text: `${itemCodeOf(item) || 'This item'} costs ${costMoney(itemCost.raw)} on the item, but the item has no unit — what is that cost per? Until you say, it is not put on this line.`,
              control: (
                <span className="cost-unit-ask">
                  <Dropdown
                    compact
                    value={line._costUnitChoice || ''}
                    onChange={(v) => patch(index, { _costUnitChoice: v })}
                    placeholder="Per…"
                    options={costUnitChoices(unitKg)}
                    {...MENU_ESCAPE}
                  />
                  <TickCheckbox
                    id={`cost-unit-save-${line.id}`}
                    checked={line._costUnitSave !== false}
                    onChange={(e) => patch(index, { _costUnitSave: e.target.checked })}
                    label="Save on the item for next time"
                    size={14}
                  />
                  {costUnitError[line.id] ? <span className="cost-unit-error">{costUnitError[line.id]}</span> : null}
                </span>
              ),
              actions: [{
                key: 'use', icon: 'check', label: 'Use this cost',
                disabled: !(Number(line._costUnitChoice) > 0),
                title: Number(line._costUnitChoice) > 0
                  ? `${costMoney(itemCost.raw)} a ${perUnitWords(Number(line._costUnitChoice))}`
                  : 'Choose what the cost is per first',
                onClick: () => answerCostUnit(index)
              }]
            } : null;
            const flags = costUnitFlag ? [costUnitFlag, ...flagsOf(line)] : flagsOf(line);
            const worst = ['error', 'query', 'warn', 'info'].find(t => flags.some(f => (f.tone || 'error') === t));
            const rowClass = [
              locked ? 'is-locked' : '',
              line._highlight ? 'is-highlight' : '',
              flags.length ? `has-flag has-flag--${worst || 'error'}` : '',
            ].filter(Boolean).join(' ') || undefined;
            const heading = typeof groupHeader === 'function' ? groupHeader(line, index) : null;

            return (
              <React.Fragment key={line.id}>
              {heading && (
                <tr className="order-grid-group">
                  <td colSpan={columns.length + 1}>{heading}</td>
                </tr>
              )}
              <tr className={rowClass} title={disabledHint || undefined}>
                {has('item') && (
                  <td className="col-item">
                    {withChoice(line, 'item', <OrderItemPicker
                      party={config.party}
                      value={line.inventory_item_id}
                      // Code and name travel separately so the trigger can badge
                      // the code, as the list does. A line whose item is not in
                      // this list (an old document, say) still shows what it was
                      // saved with.
                      displayCode={item ? itemCodeOf(item) : (line.product_code || '')}
                      displayName={item ? itemNameOf(item) : (line.product_name || '')}
                      items={inventoryItems}
                      previousItems={previousItems}
                      onRemovePrevious={onRemovePrevious}
                      contracts={contracts}
                      spotPrices={spotPrices}
                      milkPowderContracts={milkPowderContracts}
                      linkedLines={linkedLines}
                      deliveryDate={deliveryDate}
                      periodName={periodName}
                      disabled={rowDisabled}
                      onSelect={(picked, opts) => handleItemSelect(index, picked, opts)}
                      onAddItem={() => onAddItem?.(index)}
                    />, { picker: true })}
                    {locked && (
                      <span className="order-grid-lock" title={closed
                        ? 'A purchase order was raised against this line. A correction here (words, item, supplier) is made on it too, in place, for our records — nothing is sent to the supplier.'
                        : 'A purchase order has been raised against this line. Saving a change here updates it — and if the supplier already has it, you will be offered to send the revised version.'}>
                        <span className="material-symbols-rounded">link</span>
                        PO raised
                      </span>
                    )}
                    {line._hint && <span className="order-grid-hint">{line._hint}</span>}
                    {typedLine && variant === 'order' && (
                      <div className="typed-accounts">
                        <TypedAccountPicker
                          label="Sales account"
                          value={line[LINE_SALES_ACCOUNT_FIELD]}
                          list={accounts}
                          onChange={(code) => patch(index, { [LINE_SALES_ACCOUNT_FIELD]: code })}
                          missing={orderAccountsMissing.sales}
                          missingText={SALES_ACCOUNT_PROBLEM_TEXT}
                          disabled={rowDisabled || !!accountsUnavailable}
                          title={accountsUnavailable || undefined}
                        />
                        <TypedAccountPicker
                          label="Purchase account"
                          value={line[LINE_PURCHASE_ACCOUNT_FIELD]}
                          list={purchaseAccounts}
                          onChange={(code) => patch(index, { [LINE_PURCHASE_ACCOUNT_FIELD]: code })}
                          missing={orderAccountsMissing.purchase}
                          missingText={PURCHASE_ACCOUNT_PROBLEM_TEXT}
                          disabled={rowDisabled || !!accountsUnavailable || fromStockSupplier}
                          title={accountsUnavailable
                            || (fromStockSupplier ? 'From our stock — nothing is bought for this line' : undefined)
                            || (!line.supplier_contact_id && !accountCodeOf(line[LINE_PURCHASE_ACCOUNT_FIELD])
                              ? 'Needed once a supplier is chosen — what its PO line and the bill are booked to' : undefined)}
                        />
                      </div>
                    )}
                    {typedLine && variant === 'purchase' && (
                      <div className="typed-accounts">
                        <TypedAccountPicker
                          label="Purchase account"
                          value={line.account_code}
                          list={accounts}
                          onChange={(code) => patch(index, { account_code: code })}
                          missing={docAccountMissing}
                          missingText={DOCUMENT_ACCOUNT_PROBLEM_TEXT}
                          disabled={rowDisabled || !!poAccountFixed}
                          title={poAccountFixed || undefined}
                        />
                      </div>
                    )}
                  </td>
                )}

                {has('desc') && variant === 'order' && (
                  <td className="col-desc" ref={(td) => growDescription(td)}>
                    <DescriptionPair
                      line={line}
                      disabled={rowDisabled}
                      fromStock={isStockSupplier(suppliers.find(x => x.id === line.supplier_contact_id))}
                      onSale={(text) => patch(index, {
                        product_name: text,
                        // A typed line (no item) keeps its purchase text in step
                        // while the two still match. On an item line the sale
                        // text never touches the purchase text.
                        ...(!line.inventory_item_id
                          && (line.purchase_description || '') === (line.product_name || '')
                          ? { purchase_description: text } : {}),
                      })}
                      onPurchase={(text) => patch(index, { purchase_description: text })}
                    />
                  </td>
                )}

                {has('desc') && variant !== 'order' && (
                  <td className="col-desc" ref={(td) => growDescription(td)}>
                    {/* A textarea so it can wrap, but the VALUE stays one line:
                        this is the line's description on the document and in
                        Xero, where a line break would split a description in two.
                        Enter is swallowed and pasted newlines become spaces. */}
                    {withChoice(line, 'desc', <Textarea
                      rows={1}
                      value={line.product_name || ''}
                      onChange={(e) => patch(index, { product_name: e.target.value.replace(/\r?\n/g, ' ') })}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                      onInput={(e) => autoGrow(e.target)}
                      placeholder="Description"
                      disabled={rowDisabled}
                    />)}
                  </td>
                )}

                {has('additives') && (
                  <td className="col-additives" title={closedTitle('additives')}>
                    <AdditiveMultiPicker
                      supplierAccountId={additiveSupplier || null}
                      value={line.additives || []}
                      onChange={(next) => handleAdditivesChange(index, next)}
                      disabled={rowDisabled || !additivesOk || isClosed('additives')}
                      hideTags
                      {...MENU_ESCAPE}
                      placeholder={
                        !additivesOk && (line.inventory_item_id || line.account_code)
                          ? '-'
                          : undefined
                      }
                    />
                  </td>
                )}

                {has('supplier') && (
                  <td className="col-supplier">
                    <SearchableDropdown
                      value={line.supplier_contact_id}
                      onChange={(v) => {
                        const s = suppliers.find(x => x.id === v) || null;
                        patch(index, {
                          supplier_contact_id: v ?? null,
                          supplier_name: s?.company_name ?? null,
                          supplier_account_no: s?.account_no ?? null,
                          // Additives are scoped to a supplier; keeping another
                          // supplier's recipe on the line would quietly send the
                          // new one codes it does not have.
                          additives: v === line.supplier_contact_id ? line.additives : [],
                          // A new supplier brings its own haulage rate; a typed
                          // one was about the old supplier.
                          ...(haulage && v !== line.supplier_contact_id ? {
                            haulage_rate: haulage.rates?.[v] ? Number(haulage.rates[v].rate).toFixed(2) : '',
                            haulage_overridden: false,
                          } : {}),
                        });
                      }}
                      // the line's item's supplier (or its group's depots) pinned first
                      options={pinSupplierOptions(supplierOptions, pinnedSupplierIds([itemFor(line)]))}
                      placeholder="Supplier"
                      disabled={rowDisabled}
                      compact
                      {...MENU_ESCAPE}
                    />
                    {haulage && line.supplier_contact_id && (line.inventory_item_id || line.product_name) && (
                      <LineHaulage
                        line={line}
                        noWeight={noWeight}
                        haulage={haulage}
                        perPallet={perPallet}
                        disabled={rowDisabled}
                        onRate={(v) => patch(index, { haulage_rate: v, haulage_overridden: true })}
                        onReset={() => {
                          const found = haulage.rates?.[line.supplier_contact_id];
                          patch(index, { haulage_rate: found ? Number(found.rate).toFixed(2) : '', haulage_overridden: false });
                        }}
                      />
                    )}
                  </td>
                )}

                {has('transport') && transportFixedLabel && (
                  <td className="col-transport">
                    {/* Customer Collection: three disabled cells reading the
                        label, so the row keeps its shape and says why. */}
                    <div className="transport-stack" title="The customer collects this order — there is no haulier, driver or vehicle">
                      {['Haulier', 'Driver', 'Vehicle'].map(what => (
                        <SearchableDropdown
                          key={what}
                          value={transportFixedLabel}
                          onChange={() => {}}
                          options={[{ value: transportFixedLabel, label: transportFixedLabel }]}
                          placeholder={what}
                          disabled
                          compact
                          {...MENU_ESCAPE}
                        />
                      ))}
                    </div>
                  </td>
                )}

                {has('transport') && !transportFixedLabel && (
                  <td className="col-transport">
                    {/* Haulier over driver over vehicle, one cell: they are one
                        decision — who is taking this load — and three columns of
                        it pushed the prices off a laptop screen. A driver brings
                        their haulier and vehicle (utils/transportAutofill), so an
                        empty cell asks for the driver first (TransportStack). */}
                    <TransportStack
                      haulier={line.haulier || ''}
                      driver={line.driver || ''}
                      vehicle={line.vehicle || ''}
                      onChange={(changes) => patch(index, changes)}
                      hauliers={hauliers}
                      drivers={drivers}
                      vehicles={vehicles}
                      disabled={rowDisabled || !!transportLockedReason}
                      title={transportLockedReason}
                      menuProps={{ compact: true, ...MENU_ESCAPE }}
                    />
                  </td>
                )}

                {has('po') && (
                  <td className="col-po">
                    <SearchableDropdown
                      value={line.purchase_order_id ?? null}
                      // Choosing a PO by hand says nothing about which of its
                      // lines this is, so the line key goes with any change.
                      onChange={(v) => patch(index, v === line.purchase_order_id ? {} : {
                        purchase_order_id: v ?? null,
                        po_line_key: null,
                        purchase_order_number: (poOptions.find(o => o.value === v) || {}).label || null,
                      })}
                      options={[{ value: null, label: 'No PO' }, ...poOptions]}
                      placeholder="No PO"
                      searchPlaceholder="Search PO..."
                      disabled={rowDisabled}
                      compact
                      {...MENU_ESCAPE}
                    />
                  </td>
                )}

                {has('qty') && (
                  <td className="col-num" title={closedTitle('qty')}>
                    <div className="qty-cell">
                      {withChoice(line, 'qty', <DecimalInput
                        min="0"
                        value={line.quantity}
                        onValueChange={(v) => patch(index, { quantity: v })}
                        // A line counted in packs is a count: "40", not "40.00"
                        // (Cam, 2026-10-08); tonnes keep the quantity rule.
                        // A None line is a plain count too ("1").
                        format={countQty ? formatCountInput : formatDecimalInput}
                        placeholder="0"
                        disabled={rowDisabled || isClosed('qty')}
                        title={closedTitle('qty')}
                      />)}
                      {/* What a round number of pallets looks like, for items
                          that record it. Nothing at all when they don't, so the
                          row keeps its height and the column stays quiet. */}
                      {palletQty ? (
                        <span className="qty-pallet-hint">Pallet = {palletQty}</span>
                      ) : null}
                      {/* Supplied, under what was ordered (a PO's line only).
                          The line's amount is worked on this figure. With
                          the tonnes it comes to where the line is in packs
                          (utils/qtyWithTonnes — Cam, 20 Oct 2026). */}
                      {line.supplied_quantity != null && line.supplied_quantity !== '' ? (
                        <span className="qty-supplied-hint" title="Supplied — the line is costed on this">
                          Supplied {countQty ? formatCount(line.supplied_quantity) : formatQuantity(line.supplied_quantity)}
                          {(() => {
                            const v = quantityWithTonnes(line.supplied_quantity, { unitWeightKg: line.unit_weight_kg, itemUnitWeightKg: item?.unit_weight_kg });
                            return v.kind === 'packs' ? ` · ${v.tonnesText}` : '';
                          })()}
                        </span>
                      ) : null}
                      {/* An invoice made from an order: is this the supplied
                          quantity, or the ordered one with none recorded? */}
                      {variant === 'invoice' && line.supplied_recorded !== undefined ? (
                        line.supplied_recorded
                          ? <span className="qty-source-hint" title="The supplied quantity recorded on the order — what is invoiced">Supplied</span>
                          : <span className="qty-source-hint not-recorded" title="No supplied quantity was recorded on the order, so the quantity ordered is invoiced. Record what went first (Add / Update Supplied Qty) if it differs.">Ordered — no supplied qty</span>
                      ) : null}
                      {/* What the quantity comes to in tonnes, where the row
                          has no Weight column to say so: an invoice line in
                          packs, a bill line billed per kg. Never inside the
                          input — it keeps what is typed. */}
                      {(() => {
                        if (variant !== 'invoice' && variant !== 'bill') return null;
                        const v = quantityWithTonnes(line.quantity, variant === 'bill'
                          ? { unit: line.unit }
                          : { unitWeightKg: line.unit_weight_kg, itemUnitWeightKg: item?.unit_weight_kg });
                        const show = (variant === 'invoice' && v.kind === 'packs') || (variant === 'bill' && v.kind === 'kg');
                        return show && v.tonnesText
                          ? <span className="qty-tonnes-hint" title={v.title || undefined}>= {v.tonnesText}</span>
                          : null;
                      })()}
                    </div>
                  </td>
                )}

                {has('units') && (
                  <td className={`col-units${unitMissing ? ' unit-missing' : ''}`}>
                    <div title={unitsClosed || unitsUnavailable || (unitMissing ? UNIT_PROBLEM_TEXT : null) || (noWeight
                      ? 'No weight — a service or weighbridge charge: it adds nothing to tonnage and needs no pallets'
                      : (weightKgOf(line.unit_weight_kg) ? 'Set on this line' : (itemKg ? "The item's unit" : 'No unit on the item — choose one')))}>
                    <Dropdown
                      compact
                      value={noWeight ? NO_WEIGHT_UNIT : (line.unit_is_other ? OTHER_UNIT : (unitKg ? unitValueFor(unitKg) : ''))}
                      onChange={(value) => {
                        if (value === NO_WEIGHT_UNIT) {
                          // No weight, and so no pallets (Cam, 2026-10-12).
                          patch(index, noWeightUnits());
                          return;
                        }
                        // Leaving None: the 0 pallets it set go with it.
                        const fromNone = noWeight ? { pallets: '', qty_per_pallet: '' } : {};
                        if (value === OTHER_UNIT) {
                          // Start "Other" from what the line already knows.
                          patchUnits(index, {
                            ...fromNone,
                            unit_is_other: true,
                            // A None line had no weight to start from.
                            ...(noWeight ? { unit_weight_kg: '' } : {}),
                            pallet_weight_kg: unitKg && perPallet ? String(Math.round(unitKg * perPallet * 100) / 100) : '',
                            qty_per_pallet: perPallet ? String(perPallet) : ''
                          });
                          return;
                        }
                        // Picking the item's own unit goes back to following the
                        // item — except on an order or PO line, which keeps its
                        // unit on the line (Cam, 2026-10-28: explicit).
                        const explicit = variant === 'order' || variant === 'purchase';
                        patchUnits(index, {
                          ...fromNone,
                          unit_is_other: false,
                          pallet_weight_kg: '',
                          unit_weight_kg: !explicit && itemKg && Number(value) === itemKg ? '' : value
                        });
                      }}
                      placeholder="Select…"
                      // An invoice has no pallets, so no "Other — by the pallet".
                      options={[...unitOptionsFor(line.unit_is_other || noWeight ? '' : unitKg)
                        .filter(o => variant !== 'invoice' || o.value !== OTHER_UNIT),
                        { value: NO_WEIGHT_UNIT, label: NO_WEIGHT_LABEL }]}
                      disabled={rowDisabled || !!unitsUnavailable || !!unitsClosed}
                      {...MENU_ESCAPE}
                    />
                    </div>
                    {line.unit_is_other && (
                      <div className="unit-other-fields">
                        <Input
                          type="number" min="0" step="0.01"
                          value={line.qty_per_pallet ?? ''}
                          placeholder="Qty/pallet"
                          title={unitsClosed || 'Quantity per pallet'}
                          disabled={rowDisabled || !!unitsClosed}
                          onChange={(e) => {
                            const per = e.target.value;
                            const kg = pos(line.pallet_weight_kg) && pos(per) ? pos(line.pallet_weight_kg) / pos(per) : null;
                            patch(index, { qty_per_pallet: per, unit_weight_kg: kg ? String(Math.round(kg * 10000) / 10000) : '' });
                          }}
                        />
                        <Input
                          type="number" min="0" step="0.01"
                          value={line.pallet_weight_kg ?? ''}
                          placeholder="Pallet kg"
                          title={unitsClosed || 'Weight of one pallet, kg'}
                          disabled={rowDisabled || !!unitsClosed}
                          onChange={(e) => {
                            const pw = e.target.value;
                            const kg = pos(pw) && pos(line.qty_per_pallet) ? pos(pw) / pos(line.qty_per_pallet) : null;
                            patch(index, { pallet_weight_kg: pw, unit_weight_kg: kg ? String(Math.round(kg * 10000) / 10000) : '' });
                          }}
                        />
                      </div>
                    )}
                  </td>
                )}

                {has('weight') && (
                  <td className="col-num">
                    <CommitInput
                      min="0" step="0.001"
                      value={lineTonnes != null ? formatDecimalInput(lineTonnes) : ''}
                      placeholder="—"
                      title={unitsClosed || (noWeight ? 'No weight — Units is None' : (qtyNum ? 'Tonnes — type to set this line’s weight' : 'Enter a quantity first'))}
                      disabled={rowDisabled || !qtyNum || noWeight || !!unitsClosed}
                      onCommit={(text) => {
                        const t = parseFloat(text);
                        // Typing a weight sets the line's unit weight; clearing
                        // it goes back to the item's.
                        if (!Number.isFinite(t) || t <= 0) {
                          patchUnits(index, { unit_weight_kg: '', unit_is_other: false, pallet_weight_kg: '' });
                          return;
                        }
                        patchUnits(index, { unit_weight_kg: String(Math.round((t * 1000 / qtyNum) * 10000) / 10000), unit_is_other: false, pallet_weight_kg: '' });
                      }}
                    />
                  </td>
                )}

                {has('pallets') && (
                  <td className="col-num col-pallets">
                    <DecimalInput
                      min="0"
                      value={line.pallets}
                      onValueChange={(v) => patch(index, { pallets: v })}
                      // Pallets are a count: only the places they have ("4",
                      // "2.5"), not the quantity rule's 2dp (Cam, 2026-10-08).
                      format={formatCountInput}
                      // The worked-out count shows until one is typed.
                      placeholder={derivedPallets != null ? formatCountInput(derivedPallets) : '—'}
                      title={unitsClosed || (noWeight ? 'No pallets — Units is None (no weight)'
                        : (perPallet ? `Quantity ÷ ${formatCount(perPallet)} per pallet — type to set` : 'No qty per pallet — type a count'))}
                      disabled={rowDisabled || noWeight || !!unitsClosed}
                    />
                  </td>
                )}

                {has('cost') && (
                  <td className="col-num" title={closedTitle('cost') || (costBlank && itemCost && itemCost.cost !== null
                    ? `From the item${itemCost.note ? `: ${itemCost.note}` : ` (${costMoney(itemCost.cost)})`} — type to set this line's own cost`
                    : undefined)}>
                    <DecimalInput
                      min="0"
                      value={line.cost_price}
                      onValueChange={(v) => patch(index, { cost_price: v })}
                      // Blank = the item's cost, per this line's unit (what the
                      // PO is raised at — utils/itemCostUnit).
                      placeholder={costBlank && itemCost && itemCost.cost !== null ? formatDecimalInput(itemCost.cost) : '0.00'}
                      disabled={rowDisabled || isClosed('cost')}
                    />
                    {unitHint(costBlank && itemCost ? itemCost.cost : line.cost_price)}
                  </td>
                )}

                {has('price') && (
                  <td className="col-num" title={closedTitle('price')}>
                    {withChoice(line, 'price', <DecimalInput
                      value={line.unit_price}
                      onValueChange={(v) => patch(index, { unit_price: v })}
                      placeholder={(typeof pricePlaceholder === 'function' && pricePlaceholder(line)) || '0.00'}
                      disabled={fieldOff('price')}
                      title={closedTitle('price')}
                    />)}
                    {unitHint(line.unit_price)}
                    {priceAside && <div className="order-grid-price-aside">{priceAside(line)}</div>}
                  </td>
                )}

                {has('account') && (
                  <td className={`col-account${docAccountMissing ? ' account-missing' : ''}`}
                      title={docAccountMissing ? DOCUMENT_ACCOUNT_PROBLEM_TEXT : undefined}>
                    {withChoice(line, 'account', <SearchableDropdown
                      value={line.account_code || ''}
                      onChange={(v) => patch(index, { account_code: v || '' })}
                      options={
                        line.account_code && !accounts.some(a => String(a.code).toUpperCase() === String(line.account_code).toUpperCase())
                          ? [...accountOptions, { value: line.account_code, badge: line.account_code, label: getAccountName(line.account_code) || '' }]
                          : accountOptions
                      }
                      placeholder="Account"
                      searchPlaceholder="Search by code or name..."
                      disabled={rowDisabled}
                      compact
                      {...MENU_ESCAPE}
                    />, { picker: true })}
                  </td>
                )}

                {has('tax') && (
                  <td className="col-tax" title={closedTitle('tax')}>
                    {/* Searchable like every other menu in the row, so a
                        keyboard run through the line never meets the one
                        control that behaves differently. "20" finds 20% VAT. */}
                    {withChoice(line, 'tax', <SearchableDropdown
                      value={line.tax_type || ''}
                      onChange={(v) => patch(index, { tax_type: v || null, tax_rate: getTaxRate(v) || 0 })}
                      options={taxOptionsWith(config.taxOptions, line.tax_type)}
                      placeholder="Tax"
                      searchPlaceholder="Search..."
                      disabled={rowDisabled || isClosed('tax')}
                      compact
                      {...MENU_ESCAPE}
                    />, { picker: true })}
                  </td>
                )}

                {has('taxAmount') && <td className="col-num is-derived">{formatCurrency(tax)}</td>}
                {has('total') && (
                  <td className="col-num is-derived total">
                    {formatCurrency(gross)}
                    {config.compact && Math.abs(tax) >= 0.005 && <small className="order-grid-tax-under">VAT {formatCurrency(tax)}</small>}
                  </td>
                )}

                <td className="col-actions" title={closedTitle('remove') || removeReason || undefined}>
                  <div className="actions-cell">
                    <button
                      type="button"
                      className={`btn-icon delete${removeReason ? ' btn-disabled' : ''}`}
                      onClick={() => removeLine(index)}
                      disabled={rowDisabled || isClosed('remove') || !!removeReason}
                      title={closedTitle('remove') || removeReason || 'Remove line'}
                    >
                      <span className="material-symbols-rounded">delete</span>
                    </button>
                  </div>
                </td>
              </tr>
              {flags.length > 0 && (
                <tr className={`order-grid-notes has-flag--${worst || 'error'}`}>
                  <td colSpan={columns.length + 1}>
                    <div className="order-grid-flags">
                      {flags.map((flag, fi) => (
                      <span key={flag.key || fi} className={`order-grid-flag order-grid-flag--${flag.tone || 'error'}`}
                            role={flag.tone === 'error' ? 'alert' : 'note'}>
                        <span className="material-symbols-rounded">{flag.icon || 'error'}</span>
                        <span className="order-grid-flag-body">
                          <span>{flag.text}</span>
                          {flag.control && <span className="order-grid-flag-control">{flag.control}</span>}
                          {Array.isArray(flag.actions) && flag.actions.length > 0 && (
                            <span className="order-grid-flag-actions">
                              {flag.actions.map(a => (
                                <button key={a.key || a.label} type="button" className="order-grid-flag-action"
                                        disabled={!!a.disabled} title={a.title || undefined} onClick={a.onClick}>
                                  {a.icon && <span className="material-symbols-rounded" aria-hidden="true">{a.icon}</span>}
                                  {a.label}
                                </button>
                              ))}
                            </span>
                          )}
                        </span>
                      </span>
                      ))}
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {/* Deliberately not a .btn-slideout. This sits flush under the last row
          as the next empty one, so it has to be the same rectangle as the
          inputs — a pill that grows on hover would break the grid's edge. */}
      <div className="order-grid-footer">
        <button
          type="button"
          className="add-line-btn"
          onClick={addLine}
          disabled={disabled || !!editableFields || !!closed?.fields?.has('add')}
          title={disabledHint || (closed?.fields?.has('add') ? closed.reason : undefined)}
        >
          <span className="material-symbols-rounded">add</span>
          Add line
        </button>
        {footerRight}
      </div>
    </div>
  );
}

// The grid's number cell, for hosts that edit figures beside it (Add Bill's PO lines).
export { DecimalInput };

export default OrderLinesGrid;
